import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MagneticButton, Card3D, AnimatedText, ScrollReveal } from '@/components/ui/premium';

interface HeroContentProps {
  isMobile: boolean;
}

export const HeroContent = ({ isMobile }: HeroContentProps) => {
  return (
    <div className="container mx-auto px-4 relative z-20">
      {/* Floating decorative elements with #99B7AF */}
      <motion.div
        className="absolute top-10 right-10 w-20 h-20 rounded-full opacity-20 blur-xl bg-[#99B7AF]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-16 h-16 rounded-full opacity-30 bg-[#99B7AF]"
        animate={{
          y: [0, -10, 0],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto">
        {/* Main Layout Grid */}
        <div className={`grid ${isMobile ? 'grid-cols-1 text-center' : 'grid-cols-2 gap-12 items-center'}`}>
          {/* Left Column - Text Content */}
          <div className={`${isMobile ? 'order-1' : 'order-1'}`}>
            {/* Main headline with animated text */}
            <ScrollReveal animation="fade" delay={0.4}>
              <h1 className={`${isMobile ? 'text-4xl' : 'text-6xl lg:text-7xl'} font-spartan font-bold leading-tight mb-8 overflow-visible ${isMobile ? 'text-center' : 'text-left'}`}>
                <div className="mb-4 overflow-visible">
                  <AnimatedText
                    text="Você, sem estresse,"
                    animation="gradient"
                    className="block overflow-visible"
                  />
                </div>
                <div className="block overflow-visible">
                  <AnimatedText
                    text="e com muito mais Calma"
                    animation="typewriter"
                    delay={1}
                    className="bg-gradient-to-r from-brand-secondary via-brand-accent to-brand-secondary bg-clip-text text-transparent overflow-visible"
                  />
                </div>
              </h1>
            </ScrollReveal>

            {/* Subtitle with glass card */}
            <ScrollReveal animation="slide" direction="up" delay={0.8}>
              <Card3D className={`${isMobile ? 'inline-block' : 'inline-block'} px-8 py-4 mb-8 border-2 border-transparent hover:border-[#99B7AF]/50 border-[#99B7AF]`} 
                      intensity="medium">
                <p className={`text-lg font-spartan ${isMobile ? 'text-center' : 'text-left'} text-brand-primary font-medium flex items-center gap-2 ${isMobile ? 'justify-center' : 'justify-start'}`}>
                  <Star className="w-6 h-6 text-[#99B7AF]" />
                  Sua jornada de transformação começa aqui
                  <motion.div
                    className="w-2 h-2 rounded-full ml-2 bg-[#99B7AF]"
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </p>
              </Card3D>
            </ScrollReveal>

            {/* Description */}
            <ScrollReveal animation="fade" delay={1}>
              <p className={`${isMobile ? 'text-lg text-center' : 'text-xl lg:text-2xl text-left'} text-gray-700 font-open-sans font-light leading-relaxed mb-12 max-w-2xl`}>
                Uma jornada única de autoconhecimento e bem-estar emocional, 
                onde cada momento é cuidadosamente pensado para o seu crescimento pessoal.
              </p>
            </ScrollReveal>

            {/* CTA Buttons with magnetic effects */}
            <ScrollReveal animation="scale" delay={1.2}>
              <div className={`flex ${isMobile ? 'flex-col items-center' : 'flex-col lg:flex-row items-start'} gap-6 mb-16`}>
                <Link to="/app">
                  <MagneticButton
                    size={isMobile ? "lg" : "xl"}
                    variant="primary"
                    className={`${isMobile ? 'min-w-[280px]' : 'min-w-[320px]'} group relative overflow-hidden shadow-xl shadow-brand-primary/30 font-spartan`}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100"
                      animate={{
                        x: ['-100%', '100%']
                      }}
                      transition={{
                        duration: 0.6,
                        ease: 'easeInOut'
                      }}
                    />
                    <Sparkles className="w-6 h-6" />
                    <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold`}>Iniciar Jornada Gratuita</span>
                  </MagneticButton>
                </Link>
                
                <Link to="/personality-tests">
                  <MagneticButton
                    size={isMobile ? "lg" : "xl"}
                    variant="secondary"
                    className={`${isMobile ? 'min-w-[280px]' : 'min-w-[320px]'} border-2 hover:border-opacity-70 border-[#99B7AF] font-spartan`}
                  >
                    <Heart className="w-6 h-6 text-[#99B7AF]" />
                    <span className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>Conhecer Recursos</span>
                  </MagneticButton>
                </Link>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column - Mockup Image */}
          {!isMobile && (
            <div className="order-2">
              <ScrollReveal animation="fade" delay={0.6}>
                <motion.div
                  className="relative w-full h-full flex items-center justify-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  {/* Glow effect behind the mockup */}
                  <motion.div
                    className="absolute inset-0 rounded-3xl blur-2xl opacity-20 bg-gradient-to-r from-[#99B7AF] to-brand-primary scale-150"
                    animate={{
                      scale: [1.5, 1.65, 1.5],
                      opacity: [0.2, 0.3, 0.2]
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                  />
                  
                  {/* Main mockup image - same size as previous video */}
                  <img
                    src="https://i.postimg.cc/rmNR7Yt3/Vive-Sau-de-I-cone-2-3.png"
                    alt="MetroCalma App Mockup"
                    className="relative z-10 w-full h-auto max-w-4xl mx-auto drop-shadow-2xl transform scale-150"
                  />
                  
                  {/* Floating stats badge */}
                  <motion.div
                    className="absolute -top-6 -right-6 z-20"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 2, duration: 0.8 }}
                  >
                    <Card3D className="p-4 text-center bg-white/95 backdrop-blur-sm" intensity="medium">
                      <div className="text-2xl font-spartan font-bold text-brand-primary">
                        50k+
                      </div>
                      <div className="text-sm font-open-sans text-gray-600">
                        Vidas transformadas
                      </div>
                      <motion.div
                        className="w-full h-1 rounded-full mt-2 bg-brand-primary"
                        animate={{
                          scaleX: [0, 1, 0]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                    </Card3D>
                  </motion.div>
                </motion.div>
              </ScrollReveal>
            </div>
          )}

          {/* Mobile Mockup - Below text content */}
          {isMobile && (
            <div className="order-2 mt-8">
              <ScrollReveal animation="fade" delay={0.6}>
                <motion.div
                  className="relative max-w-2xl mx-auto"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  {/* Glow effect behind the mockup */}
                  <motion.div
                    className="absolute inset-0 rounded-3xl blur-xl opacity-20 bg-gradient-to-r from-[#99B7AF] to-brand-primary scale-150"
                    animate={{
                      scale: [1.5, 1.65, 1.5],
                      opacity: [0.2, 0.3, 0.2]
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                  />
                  
                  {/* Main mockup image - same size as previous video */}
                  <img
                    src="https://i.postimg.cc/rmNR7Yt3/Vive-Sau-de-I-cone-2-3.png"
                    alt="MetroCalma App Mockup"
                    className="relative z-10 w-full h-auto drop-shadow-2xl transform scale-150"
                  />
                </motion.div>
              </ScrollReveal>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
